import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  CalendarDays,
  Clock3,
  Info,
  Layers3,
  PackageSearch,
  RefreshCw,
  ShoppingBasket,
  ShoppingCart,
  Store as StoreIcon,
  TrendingDown,
  TrendingUp,
  Users,
} from 'lucide-react';
import {
  ChartDataPoint,
  DashboardSummary,
  MonthlySalesDataPoint,
  WeeklyFocusReport,
  WeeklySalesDataPoint,
  getWeeklyFocusReport,
} from '../../api/dashboard';
import { getOrderFunnelAnalytics } from '../../api/order-funnel';
import { getSellerDashboardPage } from '../../api/seller-pages';
import {
  OrderFunnelAnalytics,
  OrderFunnelEventType,
  Store,
} from '../../types';

type ContextPeriod = 'weekly' | 'monthly';
type PeriodPoint = WeeklySalesDataPoint | MonthlySalesDataPoint;

const FUNNEL_STAGES: Array<{
  eventType: OrderFunnelEventType;
  label: string;
}> = [
  { eventType: 'KAKAO_LINK_CLICKED', label: '카톡 클릭' },
  { eventType: 'ORDER_SHEET_VIEWED', label: '주문지 진입' },
  { eventType: 'CART_UPDATED', label: '상품 관심' },
  { eventType: 'ORDER_CREATE_SUCCESS', label: '주문 성공' },
];

const formatCurrency = (value: number) => `${value.toLocaleString('ko-KR')}원`;

const formatCompactCurrency = (value: number) => {
  if (value >= 100000000) {
    const amount = value / 100000000;
    return `${Number.isInteger(amount) ? amount.toFixed(0) : amount.toFixed(1)}억원`;
  }

  if (value >= 10000) {
    const amount = value / 10000;
    return `${Number.isInteger(amount) ? amount.toFixed(0) : amount.toFixed(1)}만원`;
  }

  return formatCurrency(value);
};

const formatSignedPercent = (value: number) =>
  `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;

const getActiveAmount = (point: {
  completedAmount: number;
  pendingAmount: number;
}) => point.completedAmount + point.pendingAmount;

const getPeriodKey = (point: PeriodPoint) =>
  'weekStart' in point ? point.weekStart : point.monthStart;

const toDateValue = (date: Date) => {
  const localTime = date.getTime() - date.getTimezoneOffset() * 60 * 1000;
  return new Date(localTime).toISOString().slice(0, 10);
};

const getFunnelSince = () =>
  toDateValue(new Date(Date.now() - 27 * 24 * 60 * 60 * 1000));

const getFunnelUntil = () => toDateValue(new Date());

const getFallbackWeeklyRevenue = (chartData: ChartDataPoint[]) =>
  chartData.reduce((sum, point) => sum + getActiveAmount(point), 0);

const getTrendChange = (points: PeriodPoint[]) => {
  const activePoints = points
    .map(getActiveAmount)
    .filter((value) => Number.isFinite(value));

  if (activePoints.length < 2 || activePoints[0] <= 0) {
    return null;
  }

  return ((activePoints[activePoints.length - 1] - activePoints[0]) /
    activePoints[0]) * 100;
};

const getToneClasses = (severity?: string) => {
  if (severity === 'critical') return 'border-red-100 bg-red-50 text-red-700';
  if (severity === 'warning') return 'border-orange-100 bg-orange-50 text-orange-700';
  if (severity === 'opportunity') return 'border-emerald-100 bg-emerald-50 text-emerald-700';
  return 'border-blue-100 bg-blue-50 text-blue-700';
};

interface DashboardTabProps {
  onViewOrders: () => void;
  onViewProducts: () => void;
  refreshToken?: number;
}

export const DashboardTab: React.FC<DashboardTabProps> = ({
  onViewOrders,
  onViewProducts,
  refreshToken = 0,
}) => {
  const [stores, setStores] = useState<Store[]>([]);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [weeklyReport, setWeeklyReport] = useState<WeeklyFocusReport | null>(null);
  const [allStoreReport, setAllStoreReport] = useState<WeeklyFocusReport | null>(null);
  const [funnelAnalytics, setFunnelAnalytics] = useState<OrderFunnelAnalytics | null>(null);
  const [selectedStoreId, setSelectedStoreId] = useState('all');
  const [contextPeriod, setContextPeriod] = useState<ContextPeriod>('weekly');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const requestIdRef = useRef(0);

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    const fetchDashboard = async () => {
      if (requestId > 1) setRefreshing(true);
      else setLoading(true);
      setErrorMessage('');

      const storeId = selectedStoreId === 'all' ? undefined : selectedStoreId;
      const [pageResult, reportResult, funnelResult] = await Promise.allSettled([
        getSellerDashboardPage(storeId),
        getWeeklyFocusReport({ storeId }),
        getOrderFunnelAnalytics({
          storeId,
          since: getFunnelSince(),
          until: getFunnelUntil(),
          takeSessions: 20,
        }),
      ]);

      if (requestId !== requestIdRef.current) return;

      if (pageResult.status === 'rejected') {
        console.error('Failed to fetch dashboard page:', pageResult.reason);
        setErrorMessage('대시보드 데이터를 불러오지 못했습니다.');
        setLoading(false);
        setRefreshing(false);
        return;
      }

      setStores(pageResult.value.stores);
      setSummary(pageResult.value.summary);

      if (reportResult.status === 'fulfilled') {
        setWeeklyReport(reportResult.value);
        if (selectedStoreId === 'all') setAllStoreReport(reportResult.value);
      } else {
        console.error('Failed to fetch weekly report:', reportResult.reason);
        setWeeklyReport(null);
      }

      if (funnelResult.status === 'fulfilled') {
        setFunnelAnalytics(funnelResult.value);
      } else {
        console.error('Failed to fetch funnel analytics:', funnelResult.reason);
        setFunnelAnalytics(null);
      }

      setLoading(false);
      setRefreshing(false);
    };

    void fetchDashboard();
  }, [refreshToken, selectedStoreId]);

  const selectedStoreLabel = selectedStoreId === 'all'
    ? '전체 매장'
    : stores.find((store) => store.id === selectedStoreId)?.name || '선택 매장';
  const revenueComparison = weeklyReport?.weekOverWeek.find((item) => item.label === '매출');
  const orderComparison = weeklyReport?.weekOverWeek.find((item) => item.label === '주문');
  const aovComparison = weeklyReport?.weekOverWeek.find((item) => item.label === '객단가');
  const activeRevenue = weeklyReport?.kpis.activeRevenue || getFallbackWeeklyRevenue(summary?.chartData || []);
  const purchasingCustomers = weeklyReport?.customerPattern.purchasingCustomers || weeklyReport?.kpis.activeOrders || 0;
  const averageOrderValue = weeklyReport?.kpis.averageOrderValue || 0;
  const averageBasket = weeklyReport?.customerPattern.averageBasketSkuCount || 0;
  const storeAnalysis = allStoreReport?.storeAnalysis || weeklyReport?.storeAnalysis || [];
  const periodPoints: PeriodPoint[] = contextPeriod === 'weekly'
    ? summary?.weeklySalesData || []
    : summary?.monthlySalesData || [];
  const maxPeriodValue = Math.max(
    ...periodPoints.map((point) => point.completedAmount + point.pendingAmount + point.cancelledAmount),
    1,
  );
  const longTermChange = getTrendChange(periodPoints);
  const bestPeriod = [...periodPoints].sort((a, b) => getActiveAmount(b) - getActiveAmount(a))[0];
  const funnelSteps = FUNNEL_STAGES.map((stage) => ({
    ...stage,
    data: funnelAnalytics?.steps.find((step) => step.eventType === stage.eventType),
  }));
  const firstFunnelSessions = funnelSteps.find((step) => step.data)?.data?.uniqueSessions || 0;

  const hourlyBuckets = useMemo(() => {
    const hourlyStats = summary?.hourlyStats || [];
    return Array.from({ length: 6 }, (_, bucketIndex) => {
      const startHour = bucketIndex * 4;
      const endHour = startHour + 3;
      const matchingHours = hourlyStats.filter((item) => item.hour >= startHour && item.hour <= endHour);
      return {
        label: `${String(startHour).padStart(2, '0')}~${String(endHour).padStart(2, '0')}시`,
        orders: matchingHours.reduce((sum, item) => sum + item.count, 0),
        quantity: matchingHours.reduce((sum, item) => sum + item.quantity, 0),
      };
    });
  }, [summary?.hourlyStats]);
  const maxHourlyOrders = Math.max(...hourlyBuckets.map((item) => item.orders), 1);
  const productOpportunities = useMemo(
    () => [...(funnelAnalytics?.productInsights || [])]
      .sort((a, b) => b.opportunityScore - a.opportunityScore)
      .slice(0, 5),
    [funnelAnalytics?.productInsights],
  );
  const pendingItemQuantity = summary?.stats.pendingItemQuantity || 0;
  const pendingOrders = summary?.stats.pendingOrders || 0;

  const ruleSignals = useMemo(() => {
    const signals: Array<{
      title: string;
      description: string;
      severity: 'info' | 'opportunity' | 'warning' | 'critical';
      action?: 'orders' | 'products';
    }> = [];
    const primaryInsight = funnelAnalytics?.insights?.[0];

    if (primaryInsight) {
      signals.push({
        title: primaryInsight.title,
        description: `${primaryInsight.description} ${primaryInsight.action}`,
        severity: primaryInsight.severity,
        action: primaryInsight.title.includes('상품') ? 'products' : undefined,
      });
    }

    (weeklyReport?.actionItems || []).slice(0, 2).forEach((actionItem) => {
      signals.push({
        title: actionItem,
        description: '이번 주 매출·고객·상품 조건을 사전에 정의한 기준으로 확인했습니다.',
        severity: 'opportunity',
        action: actionItem.includes('상품') || actionItem.includes('묶음') ? 'products' : undefined,
      });
    });

    if (pendingItemQuantity > 0) {
      signals.push({
        title: `포장 대기 상품 ${pendingItemQuantity.toLocaleString()}개`,
        description: `${pendingOrders.toLocaleString()}건의 주문이 아직 준비 전 상태입니다.`,
        severity: 'warning',
        action: 'orders',
      });
    }

    if (signals.length === 0) {
      signals.push({
        title: '현재 기준을 넘은 운영 신호가 없습니다',
        description: '데이터가 더 쌓이거나 기준을 넘는 변화가 생기면 이 영역에 표시합니다.',
        severity: 'info',
      });
    }

    return signals.slice(0, 4);
  }, [funnelAnalytics?.insights, pendingItemQuantity, pendingOrders, weeklyReport?.actionItems]);

  if (loading && !summary) {
    return <div className="rounded-[28px] border border-gray-100 bg-white p-12 text-center text-sm font-bold text-gray-400 shadow-sm">운영 대시보드 데이터를 불러오는 중입니다...</div>;
  }

  if (!summary) {
    return (
      <div className="rounded-[28px] border border-red-100 bg-white p-10 text-center shadow-sm">
        <AlertTriangle className="mx-auto mb-3 text-red-500" size={28} />
        <p className="font-black text-gray-900">{errorMessage || '대시보드 데이터를 불러오지 못했습니다.'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="rounded-[28px] border border-gray-100 bg-white p-5 shadow-sm md:p-7">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-emerald-50 px-3 py-1 text-[10px] font-black text-emerald-700">공동구매 운영판</span>
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-[10px] font-black text-blue-700"><Info size={12} /> 데이터 규칙 자동 분석</span>
              {refreshing && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-gray-400"><RefreshCw className="animate-spin" size={12} /> 갱신 중</span>}
            </div>
            <h3 className="text-2xl font-black tracking-tight text-gray-900 md:text-3xl">공동구매 운영 대시보드</h3>
            <p className="mt-2 text-xs font-bold text-gray-400">전체 매장과 개별 매장을 오가며 이번 주 상태와 축적된 흐름을 함께 확인합니다.</p>
          </div>
          <label className="min-w-[220px]">
            <span className="mb-2 block text-[10px] font-black uppercase tracking-wider text-gray-400">운영 범위</span>
            <select value={selectedStoreId} onChange={(event) => setSelectedStoreId(event.target.value)} className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-black text-gray-800 outline-none transition focus:border-emerald-400 focus:bg-white">
              <option value="all">전체 매장</option>
              {stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}
            </select>
          </label>
        </div>
        <div className="mt-5 flex flex-col gap-2 rounded-2xl bg-gray-50 px-4 py-3 text-xs font-bold text-gray-500 md:flex-row md:items-center md:justify-between">
          <span className="inline-flex items-center gap-2 text-gray-800"><CalendarDays size={15} /> {selectedStoreLabel} · {weeklyReport?.period.label || '이번 주'}</span>
          <span>취소 제외 · {weeklyReport?.period.isPartial ? '진행 중인 기간' : '완료 기간'} · 직전 동일 기간 비교</span>
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-[1.35fr_0.65fr]">
        <div className="rounded-[28px] border border-gray-100 bg-white p-6 shadow-sm md:p-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div><p className="text-xs font-black text-gray-400">이번 주 유효 매출</p><p className="mt-2 text-4xl font-black tracking-tight text-gray-900 md:text-5xl">{formatCompactCurrency(activeRevenue)}</p></div>
            {revenueComparison && <span className={`w-fit rounded-full px-3 py-2 text-xs font-black ${revenueComparison.changeRate >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-orange-50 text-orange-700'}`}>직전 동일 기간 {formatSignedPercent(revenueComparison.changeRate)}</span>}
          </div>
          <div className="mt-7 grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-gray-50 p-4"><span className="text-[10px] font-black text-gray-400">변화 요인 1</span><strong className="mt-1 block text-sm font-black text-gray-900">주문 {orderComparison ? formatSignedPercent(orderComparison.changeRate) : '-'}</strong></div>
            <div className="rounded-2xl bg-gray-50 p-4"><span className="text-[10px] font-black text-gray-400">변화 요인 2</span><strong className="mt-1 block text-sm font-black text-gray-900">객단가 {aovComparison ? formatSignedPercent(aovComparison.changeRate) : '-'}</strong></div>
          </div>
        </div>

        <div className="overflow-hidden rounded-[28px] border border-gray-100 bg-white shadow-sm">
          {[
            { label: '주문 고객', value: `${purchasingCustomers.toLocaleString()}명`, detail: weeklyReport ? `신규 ${weeklyReport.customerPattern.newCustomers.toLocaleString()}명` : '-', icon: Users },
            { label: '평균 객단가', value: formatCurrency(averageOrderValue), detail: aovComparison ? formatSignedPercent(aovComparison.changeRate) : '-', icon: ShoppingBasket },
            { label: selectedStoreId === 'all' ? '운영 매장' : '평균 장바구니', value: selectedStoreId === 'all' ? `${stores.length.toLocaleString()}곳` : `${averageBasket.toFixed(1)}종`, detail: selectedStoreId === 'all' ? `${storeAnalysis.length.toLocaleString()}곳 집계` : `${(weeklyReport?.customerPattern.averageBasketQuantity || 0).toFixed(1)}개`, icon: selectedStoreId === 'all' ? StoreIcon : ShoppingCart },
          ].map((item, index) => (
            <div key={item.label} className={`grid grid-cols-[36px_1fr_auto] items-center gap-3 p-5 ${index < 2 ? 'border-b border-gray-100' : ''}`}>
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-emerald-50 text-emerald-700"><item.icon size={17} /></span>
              <div><span className="text-[10px] font-black text-gray-400">{item.label}</span><strong className="mt-1 block text-lg font-black text-gray-900">{item.value}</strong></div>
              <span className="text-[10px] font-black text-emerald-700">{item.detail}</span>
            </div>
          ))}
        </div>
      </section>

      <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-emerald-50 px-4 py-3 text-[11px] font-bold text-emerald-800">
        <BadgeCheck size={16} /><strong className="text-gray-900">데이터 상태</strong><span>{stores.length.toLocaleString()}개 매장</span><span>최근 8주·12개월 누적 비교</span><span>퍼널 {funnelAnalytics ? `${funnelAnalytics.summary.uniqueSessions.toLocaleString()}세션` : '데이터 없음'}</span>
      </div>

      <section className="rounded-[28px] border border-gray-100 bg-white p-5 shadow-sm md:p-7">
        <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-start md:justify-between"><div><h3 className="text-lg font-black text-gray-900">매장별 이번 주 상태</h3><p className="mt-1 text-xs font-bold text-gray-400">전체 성과를 만든 매장과 확인이 필요한 매장을 비교합니다.</p></div><span className="text-[10px] font-black text-gray-400">카드를 누르면 해당 매장으로 전환</span></div>
        {storeAnalysis.length > 0 ? (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {storeAnalysis.map((store) => (
              <button key={store.storeId} type="button" onClick={() => setSelectedStoreId(store.storeId)} className={`rounded-2xl border p-4 text-left transition hover:-translate-y-0.5 hover:shadow-md ${selectedStoreId === store.storeId ? 'border-blue-300 bg-blue-50' : 'border-gray-100 bg-gray-50'}`}>
                <div className="flex items-start justify-between gap-3"><div><strong className="text-sm font-black text-gray-900">{store.name}</strong><span className="mt-1 block text-[10px] font-bold text-gray-400">주문 {store.orders.toLocaleString()}건 · 객단가 {formatCompactCurrency(store.averageOrderValue)}</span></div><div className="text-right"><strong className="text-sm font-black text-gray-900">{formatCompactCurrency(store.revenue)}</strong><span className={`mt-1 block text-[10px] font-black ${store.changeRate >= 0 ? 'text-emerald-700' : 'text-orange-700'}`}>{formatSignedPercent(store.changeRate)}</span></div></div>
              </button>
            ))}
          </div>
        ) : <p className="rounded-2xl bg-gray-50 p-6 text-center text-xs font-bold text-gray-400">이번 주 비교 가능한 매장 데이터가 없습니다.</p>}
      </section>

      <section className="rounded-[28px] border border-gray-100 bg-white p-5 shadow-sm md:p-7">
        <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-start md:justify-between"><div><div className="flex items-center gap-2"><h3 className="text-lg font-black text-gray-900">오늘 확인할 일</h3><span className="rounded-full bg-blue-50 px-2 py-1 text-[9px] font-black text-blue-700">데이터 규칙 기반</span></div><p className="mt-1 text-xs font-bold text-gray-400">정해진 숫자 기준을 넘은 항목만 표시하므로 항상 같은 근거로 판단합니다.</p></div><span className="text-[10px] font-black text-gray-400">영향이 큰 순서</span></div>
        <div className="space-y-2">
          {ruleSignals.map((signal, index) => (
            <div key={`${signal.title}-${index}`} className="grid gap-3 rounded-2xl bg-gray-50 p-4 md:grid-cols-[32px_1fr_auto] md:items-center">
              <span className="grid h-8 w-8 place-items-center rounded-full bg-emerald-700 text-xs font-black text-white">{index + 1}</span>
              <div><strong className="text-sm font-black text-gray-900">{signal.title}</strong><p className="mt-1 text-[11px] font-bold leading-relaxed text-gray-400">{signal.description}</p></div>
              {signal.action && <button type="button" onClick={signal.action === 'orders' ? onViewOrders : onViewProducts} className="inline-flex items-center justify-center gap-1 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-black text-gray-700 transition hover:border-gray-300">{signal.action === 'orders' ? '주문 보기' : '상품 보기'}<ArrowRight size={14} /></button>}
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-[28px] border border-gray-100 bg-white p-5 shadow-sm md:p-7">
        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-start md:justify-between"><div><h3 className="text-lg font-black text-gray-900">축적된 데이터의 맥락</h3><p className="mt-1 text-xs font-bold text-gray-400">한 주의 증감이 장기 흐름인지 확인합니다.</p></div><div className="flex rounded-2xl border border-gray-100 bg-gray-50 p-1">{(['weekly', 'monthly'] as ContextPeriod[]).map((period) => <button key={period} type="button" onClick={() => setContextPeriod(period)} className={`rounded-xl px-4 py-2 text-xs font-black transition ${contextPeriod === period ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-400'}`}>{period === 'weekly' ? '최근 8주' : '최근 12개월'}</button>)}</div></div>
        <div className="grid gap-5 xl:grid-cols-[1.35fr_0.65fr]">
          <div className="overflow-x-auto pb-2"><div className="flex h-64 min-w-[620px] items-end gap-3 border-b border-gray-100 px-2">
            {periodPoints.map((point) => {
              const totalAmount = point.completedAmount + point.pendingAmount + point.cancelledAmount;
              const heightPercent = Math.max(6, (totalAmount / maxPeriodValue) * 100);
              const completedPercent = totalAmount > 0 ? (point.completedAmount / totalAmount) * 100 : 0;
              const pendingPercent = totalAmount > 0 ? (point.pendingAmount / totalAmount) * 100 : 0;
              return <div key={getPeriodKey(point)} className="group flex h-full min-w-[48px] flex-1 flex-col items-center justify-end gap-2"><span className="text-[9px] font-black text-emerald-700 opacity-0 transition group-hover:opacity-100">{formatCompactCurrency(getActiveAmount(point))}</span><div className="flex w-full max-w-[44px] flex-col-reverse overflow-hidden rounded-t-lg bg-gray-100" style={{ height: `${heightPercent}%` }}><span className="bg-emerald-700" style={{ height: `${completedPercent}%` }} /><span className="bg-blue-400" style={{ height: `${pendingPercent}%` }} /><span className="bg-red-300" style={{ height: `${Math.max(0, 100 - completedPercent - pendingPercent)}%` }} /></div><span className="whitespace-nowrap text-[9px] font-bold text-gray-400">{point.label}</span></div>;
            })}
          </div></div>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
            <div className="rounded-2xl bg-gray-50 p-4"><span className="text-[10px] font-black text-gray-400">장기 추세</span><strong className="mt-1 flex items-center gap-2 text-sm font-black text-gray-900">{longTermChange === null ? '비교 데이터 부족' : longTermChange >= 0 ? <><TrendingUp size={16} className="text-emerald-700" /> {formatSignedPercent(longTermChange)}</> : <><TrendingDown size={16} className="text-orange-700" /> {formatSignedPercent(longTermChange)}</>}</strong><p className="mt-1 text-[10px] font-bold text-gray-400">첫 구간과 마지막 구간의 유효 매출 비교</p></div>
            <div className="rounded-2xl bg-gray-50 p-4"><span className="text-[10px] font-black text-gray-400">가장 높은 구간</span><strong className="mt-1 block text-sm font-black text-gray-900">{bestPeriod ? `${bestPeriod.label} · ${formatCompactCurrency(getActiveAmount(bestPeriod))}` : '-'}</strong><p className="mt-1 text-[10px] font-bold text-gray-400">완료와 진행 주문을 합친 값</p></div>
            <div className="rounded-2xl bg-blue-50 p-4 sm:col-span-2 xl:col-span-1"><span className="text-[10px] font-black text-blue-600">판단 원칙</span><strong className="mt-1 block text-sm font-black text-gray-900">이번 주 수치만으로 반복 패턴을 확정하지 않습니다</strong><p className="mt-1 text-[10px] font-bold leading-relaxed text-gray-500">장기 추세와 퍼널 표본을 함께 보고, 기준을 충족한 경우에만 실행 후보로 올립니다.</p></div>
          </div>
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
        <section className="rounded-[28px] border border-gray-100 bg-white p-5 shadow-sm md:p-7">
          <div className="mb-5 flex items-start justify-between gap-3"><div><h3 className="text-lg font-black text-gray-900">카톡 공지에서 주문까지</h3><p className="mt-1 text-xs font-bold text-gray-400">최근 28일 중복 제거 세션 기준입니다.</p></div><span className="text-[10px] font-black text-gray-400">시작 {firstFunnelSessions.toLocaleString()}세션</span></div>
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">{funnelSteps.map((step) => <div key={step.eventType} className="rounded-2xl bg-gray-50 p-4"><span className="text-[10px] font-black text-gray-400">{step.label}</span><strong className="mt-2 block text-2xl font-black text-gray-900">{(step.data?.uniqueSessions || 0).toLocaleString()}</strong><span className="mt-1 block text-[10px] font-black text-emerald-700">{step.data ? `${step.data.conversionRateFromStart.toFixed(1)}%` : '-'}</span></div>)}</div>
          {funnelAnalytics?.insights?.[0] ? <div className="mt-4 rounded-2xl bg-blue-50 p-4"><strong className="text-xs font-black text-gray-900">{funnelAnalytics.insights[0].title}</strong><p className="mt-1 text-[11px] font-bold leading-relaxed text-gray-500">{funnelAnalytics.insights[0].description}</p></div> : <div className="mt-4 rounded-2xl bg-gray-50 p-4 text-xs font-bold text-gray-400">퍼널 이벤트가 더 쌓이면 가장 큰 이탈 구간을 표시합니다.</div>}
        </section>

        <section className="rounded-[28px] border border-gray-100 bg-white p-5 shadow-sm md:p-7">
          <div className="mb-5 flex items-start justify-between gap-3"><div><h3 className="text-lg font-black text-gray-900">시간대별 주문 반응</h3><p className="mt-1 text-xs font-bold text-gray-400">최근 30일 주문 건수 기준입니다.</p></div><Clock3 size={18} className="text-gray-300" /></div>
          <div className="grid grid-cols-3 gap-2">{hourlyBuckets.map((bucket) => { const intensity = bucket.orders / maxHourlyOrders; return <div key={bucket.label} className="rounded-2xl p-3" style={{ backgroundColor: `rgba(5, 150, 105, ${0.08 + intensity * 0.5})` }}><span className="text-[9px] font-black text-gray-600">{bucket.label}</span><strong className="mt-1 block text-sm font-black text-gray-900">{bucket.orders.toLocaleString()}건</strong><span className="text-[9px] font-bold text-gray-500">상품 {bucket.quantity.toLocaleString()}개</span></div>; })}</div>
        </section>
      </div>

      <section className="rounded-[28px] border border-gray-100 bg-white p-5 shadow-sm md:p-7">
        <div className="mb-5 flex flex-col gap-2 md:flex-row md:items-start md:justify-between"><div><h3 className="text-lg font-black text-gray-900">상품별 관심과 주문</h3><p className="mt-1 text-xs font-bold text-gray-400">장바구니 관심 세션과 주문 전환을 함께 비교합니다.</p></div><button type="button" onClick={onViewProducts} className="inline-flex items-center gap-1 text-xs font-black text-emerald-700">상품 관리 열기 <ArrowRight size={14} /></button></div>
        {productOpportunities.length > 0 ? (
          <div className="overflow-x-auto"><table className="min-w-[720px] w-full border-collapse text-left text-xs"><thead><tr className="border-b border-gray-100 text-[10px] font-black text-gray-400"><th className="px-3 py-3">상품</th><th className="px-3 py-3 text-right">관심 세션</th><th className="px-3 py-3 text-right">주문 전환</th><th className="px-3 py-3 text-right">전환율</th><th className="px-3 py-3 text-right">매출</th><th className="px-3 py-3 text-right">상태</th></tr></thead><tbody>{productOpportunities.map((product) => <tr key={product.productId} className="border-b border-gray-50 last:border-0"><td className="px-3 py-4 font-black text-gray-900">{product.productName}</td><td className="px-3 py-4 text-right font-bold text-gray-600">{product.interestSessions.toLocaleString()}</td><td className="px-3 py-4 text-right font-bold text-gray-600">{product.convertedSessions.toLocaleString()}</td><td className="px-3 py-4 text-right font-black text-gray-900">{product.funnelConversionRate === null ? '-' : `${product.funnelConversionRate.toFixed(1)}%`}</td><td className="px-3 py-4 text-right font-bold text-gray-600">{formatCompactCurrency(product.revenue)}</td><td className="px-3 py-4 text-right"><span className={`inline-flex rounded-full border px-2 py-1 text-[9px] font-black ${product.opportunityScore > 0 ? 'border-orange-100 bg-orange-50 text-orange-700' : 'border-emerald-100 bg-emerald-50 text-emerald-700'}`}>{product.opportunityScore > 0 ? '확인 필요' : '안정'}</span></td></tr>)}</tbody></table></div>
        ) : <div className="rounded-2xl bg-gray-50 p-8 text-center"><PackageSearch className="mx-auto mb-2 text-gray-300" size={24} /><p className="text-xs font-bold text-gray-400">상품별 퍼널 표본이 더 쌓이면 기회 상품을 표시합니다.</p></div>}
      </section>

      <section className="rounded-[28px] border border-gray-100 bg-white p-5 shadow-sm md:p-7">
        <div className="mb-5 flex items-start justify-between gap-3"><div><h3 className="text-lg font-black text-gray-900">데이터 기반 신호</h3><p className="mt-1 text-xs font-bold text-gray-400">계산된 근거를 먼저 보여주고, AI를 연결하면 이 근거로 설명이나 카톡 문구를 만들 수 있습니다.</p></div><Layers3 size={18} className="text-gray-300" /></div>
        <div className="grid gap-3 md:grid-cols-2">{(funnelAnalytics?.insights || []).slice(0, 4).map((insight) => <article key={`${insight.title}-${insight.metricValue}`} className={`rounded-2xl border p-4 ${getToneClasses(insight.severity)}`}><div className="flex items-start justify-between gap-3"><span className="text-[9px] font-black uppercase tracking-wider">규칙 기반</span><span className="text-[10px] font-black">{insight.metricLabel} {insight.metricValue}</span></div><strong className="mt-3 block text-sm font-black text-gray-900">{insight.title}</strong><p className="mt-2 text-[11px] font-bold leading-relaxed text-gray-500">{insight.description}</p></article>)}{(funnelAnalytics?.insights?.length || 0) === 0 && <div className="md:col-span-2 rounded-2xl bg-gray-50 p-8 text-center text-xs font-bold text-gray-400">최소 표본을 충족한 데이터 신호가 아직 없습니다.</div>}</div>
      </section>
    </div>
  );
};
